// @ts-nocheck
/**
 * IncomingCallPreview — modale plein ecran affichee quand l'agent clique sur
 * le corps du toast IncomingCallToast. Montre les details enrichis du
 * prospect appelant (nom, societe, client de rattachement, telephone,
 * LinkedIn si dispo) AVANT que l'agent ne decide d'accepter ou decliner.
 *
 * Differente de CallSheet qui est utilisee une fois l'appel ACCEPTE (call
 * actif, AMD verdict, transcription temps reel, qualification post-call).
 * Ici on est juste en preview : aucun call_status BDD n'est encore cree.
 *
 * UX : 2 boutons en bas, Decliner (rouge a gauche) et Repondre (vert a
 * droite, primary). L'agent peut aussi fermer la modale via la croix —
 * dans ce cas le toast reste visible avec ses propres boutons.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Mail, Linkedin, Building2 } from 'lucide-react';

interface IncomingCallLike {
  parameters?: { From?: string };
}

interface ProspectLite {
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_company?: string | null;
  prospect_email?: string | null;
  prospect_title?: string | null;
  /** The list (import source) the prospect belongs to. */
  source?: string | null;
  linkedin_url?: string | null;
  customer_id?: string | null;
}

interface CustomerLite {
  id: string;
  company_name?: string | null;
  name?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incomingCall?: IncomingCallLike | null;
  prospectQueue?: ProspectLite[];
  customers?: CustomerLite[];
  onAccept: () => void;
  onReject: () => void;
}

export default function IncomingCallPreview({
  open,
  onOpenChange,
  incomingCall,
  prospectQueue,
  customers,
  onAccept,
  onReject,
}: Props) {
  const { t } = useTranslation('dialer');
  // Cf. IncomingCallToast : on prefere les Parameters custom poses par le
  // voice-handler via TwilioDeviceContext, et on extrait un E.164 propre du SIP
  // URI si c'est ce qu'on a recu.
  const annotated = incomingCall as (IncomingCallLike & { __displayName?: string; __displayPhone?: string; __displayList?: string }) | null;
  const fromNumber = annotated?.__displayPhone || incomingCall?.parameters?.From || '';
  const fromDigits = fromNumber.replace(/\D/g, '');
  const displayPhone = (() => {
    if (!fromNumber) return '';
    const sipMatch = fromNumber.match(/^sip:(\+?\d+)@/i);
    const captured = sipMatch?.[1];
    if (captured) return captured.startsWith('+') ? captured : `+${captured}`;
    return fromNumber;
  })();

  const prospect = useMemo<ProspectLite | null>(() => {
    if (!incomingCall || !prospectQueue || !fromDigits) return null;
    const fromSuffix = fromDigits.slice(-9);
    return (
      prospectQueue.find((p) => {
        if (!p.prospect_phone) return false;
        const pp = p.prospect_phone.replace(/\D/g, '');
        return pp.slice(-9) === fromSuffix;
      }) || null
    );
  }, [incomingCall, prospectQueue, fromDigits]);

  const customerName = useMemo(() => {
    if (!prospect?.customer_id || !customers) return null;
    const c = customers.find((c) => c.id === prospect.customer_id);
    return c?.company_name || c?.name || null;
  }, [prospect, customers]);

  const displayName = annotated?.__displayName || prospect?.prospect_name || 'Appel entrant';
  const listName = prospect?.source || annotated?.__displayList || '';
  const company = prospect?.prospect_company || '';
  const title = prospect?.prospect_title || '';
  const email = prospect?.prospect_email || '';
  const linkedin = prospect?.linkedin_url || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="p-0 overflow-hidden rounded-vf-lg border"
        style={{
          width: 480,
          maxWidth: 480,
          background: 'var(--vf-surface-2)',
          borderColor: 'var(--vf-border)',
          boxShadow: 'var(--vf-shadow-lg)',
        }}
      >
        <DialogTitle className="sr-only">{t('dialer:incoming.title')}</DialogTitle>

        {/* Header — pulse vert + label */}
        <div
          className="flex items-center gap-3 px-5 py-4 border-b"
          style={{ borderColor: 'var(--vf-border)' }}
        >
          <span className="relative inline-flex items-center justify-center">
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(52, 211, 153, 0.35)', animationDuration: '1.6s' }}
            />
            <span
              className="relative inline-flex items-center justify-center rounded-full"
              style={{ width: 40, height: 40, background: 'rgba(52, 211, 153, 0.18)', color: '#34D399' }}
            >
              <Phone className="w-5 h-5" />
            </span>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{t('dialer:incoming.title')}</p>
            <p className="text-xs text-emerald-600 font-medium">{t('dialer:incoming.ringing')}</p>
          </div>
        </div>

        {/* Body : identite du prospect */}
        <div className="px-5 py-5 space-y-4">
          <div>
            <p className="text-xl font-bold text-foreground truncate">{displayName}</p>
            {listName && (
              <p className="text-sm text-foreground/80 truncate mt-0.5">{listName}</p>
            )}
            {customerName && (
              <p className="text-sm text-muted-foreground truncate mt-0.5">{customerName}</p>
            )}
          </div>

          {(company || title) && (
            <div className="flex items-start gap-2 text-sm text-foreground">
              <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                {company && <p className="truncate">{company}</p>}
                {title && <p className="text-xs text-muted-foreground truncate">{title}</p>}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm font-mono text-foreground">
            <Phone className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{displayPhone || t('dialer:incoming.unknownNumber')}</span>
          </div>

          {email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 text-sm text-[var(--vf-accent-text)] hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span className="truncate">{email}</span>
            </a>
          )}

          {linkedin && (
            <a
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[var(--vf-accent-text)] hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Linkedin className="w-4 h-4 shrink-0" />
              <span className="truncate">{linkedin}</span>
            </a>
          )}

          {!prospect && (
            <p className="text-xs text-amber-600">
              {t('dialer:incoming.unknownProspect')}
            </p>
          )}
        </div>

        {/* Footer : Decliner + Repondre */}
        <div className="px-5 py-4 border-t flex items-center gap-3" style={{ borderColor: 'var(--vf-border)' }}>
          <Button
            variant="outline"
            onClick={onReject}
            className="flex-1 h-11 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
          >
            <PhoneOff className="w-4 h-4" />
            {t('dialer:incoming.reject')}
          </Button>
          <Button
            onClick={onAccept}
            className="flex-1 h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Phone className="w-4 h-4" />
            {t('dialer:incoming.accept')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
